import React from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Pill, Sparkles, Info } from 'lucide-react';
import { Drug, CreateDrugDto, UpdateDrugDto } from '../../types/drug.types';
import { Input } from '../common/Input';
import { Button } from '../common/Button';

interface DrugFormProps {
  drug?: Drug;
  onSubmit: (data: CreateDrugDto | UpdateDrugDto) => void;
  onCancel?: () => void;
  isLoading?: boolean;
}

export const DrugForm: React.FC<DrugFormProps> = ({
  drug,
  onSubmit,
  onCancel,
  isLoading = false,
}) => {
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<CreateDrugDto>({
    defaultValues: drug
      ? {
          name: drug.name,
          description: drug.description || '',
          unit: drug.unit || '',
        }
      : undefined,
  });

  return (
    <motion.form
      onSubmit={handleSubmit(onSubmit)}
      className="space-y-4"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      {/* Info Banner */}
      <motion.div
        className="d-flex align-items-start gap-3 p-3 rounded-3 mb-4"
        style={{
          background: 'linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%)',
          border: '1px solid #7dd3fc',
        }}
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.1 }}
      >
        <Info className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
        <div>
          <p className="mb-0 fw-semibold text-primary" style={{ fontSize: '0.9rem' }}>
            {t('drugs.quickAddHint', 'Enter the drug name to add it quickly. Other fields are optional.')}
          </p>
        </div>
      </motion.div>

      {/* Drug Name - Main Field */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <label className="d-flex align-items-center gap-2 mb-2 fw-semibold text-dark" style={{ fontSize: '0.95rem' }}>
          <Pill className="h-4 w-4 text-primary" />
          {t('drugs.name', 'Drug Name')}
          <span className="text-danger">*</span>
        </label>
        <Input
          required
          error={errors.name?.message}
          autoFocus
          placeholder={t('drugs.namePlaceholder', 'e.g., Paracetamol 500mg')}
          className="form-control-lg"
          style={{
            fontSize: '1rem',
            padding: '0.75rem 1rem',
            borderRadius: '0.5rem',
            border: '2px solid #e5e7eb',
            transition: 'all 0.3s',
          }}
          {...register('name', {
            required: t('drugs.nameRequired', 'Drug name is required'),
            maxLength: {
              value: 200,
              message: t('drugs.nameMaxLength', 'Name must not exceed 200 characters'),
            },
          })}
        />
      </motion.div>

      {/* Unit and Description */}
      <div className="row g-3">
        <motion.div
          className="col-6"
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3 }}
        >
          <label className="d-flex align-items-center gap-2 mb-2 fw-semibold text-dark" style={{ fontSize: '0.95rem' }}>
            <Sparkles className="h-4 w-4 text-warning" />
            {t('drugs.unit', 'Unit')}
          </label>
          <Input
            error={errors.unit?.message}
            placeholder={t('drugs.unitPlaceholder', 'e.g., tablet, ml')}
            style={{
              fontSize: '0.95rem',
              padding: '0.65rem 1rem',
              borderRadius: '0.5rem',
              border: '2px solid #e5e7eb',
            }}
            {...register('unit', {
              maxLength: {
                value: 50,
                message: t('drugs.unitMaxLength', 'Unit must not exceed 50 characters'),
              },
            })}
          />
        </motion.div>
        <motion.div
          className="col-6"
          initial={{ opacity: 0, x: 10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3 }}
        >
          <label className="d-flex align-items-center gap-2 mb-2 fw-semibold text-dark" style={{ fontSize: '0.95rem' }}>
            <Sparkles className="h-4 w-4 text-info" />
            {t('drugs.description', 'Description')}
          </label>
          <Input
            error={errors.description?.message}
            placeholder={t('drugs.descriptionPlaceholder', 'Optional notes')}
            style={{
              fontSize: '0.95rem',
              padding: '0.65rem 1rem',
              borderRadius: '0.5rem',
              border: '2px solid #e5e7eb',
            }}
            {...register('description', {
              maxLength: {
                value: 1000,
                message: t('drugs.descriptionMaxLength', 'Description must not exceed 1000 characters'),
              },
            })}
          />
        </motion.div>
      </div>

      {/* Action Buttons */}
      <motion.div
        className="d-flex gap-3 justify-content-end mt-4 pt-3 border-top"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
      >
        {onCancel && (
          <Button
            type="button"
            variant="secondary"
            onClick={onCancel}
            disabled={isLoading}
            className="px-4"
            style={{ fontWeight: 500 }}
          >
            {t('common.cancel')}
          </Button>
        )}
        <Button
          type="submit"
          isLoading={isLoading}
          variant="primary"
          className="px-4 d-flex align-items-center gap-2"
          style={{
            fontWeight: 600,
            background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
            border: 'none',
            boxShadow: '0 4px 12px rgba(37, 99, 235, 0.3)',
          }}
        >
          <Pill className="h-4 w-4" />
          {drug ? t('common.save', 'Save') : t('common.add', 'Add Drug')}
        </Button>
      </motion.div>
    </motion.form>
  );
};

